// lib
import { Map, List } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

// components
import CheckBox from '@aytm/lib/rfd-ui/components/basic/check-box'
import InlineSelect from '@aytm/lib/rfd-ui/components/basic/inline-select'

// styles
import '@aytm/lib/rfd-ui/styles/basic/multi-select.scss'

// <Component>
MultiSelect.propTypes = {
  showSelectAllOption: PropTypes.bool,
  className: PropTypes.string,
  getLabel: PropTypes.func,
  readOnly: PropTypes.bool,
  options: PropTypes.instanceOf(List).isRequired
}
export default React.memo(
  MultiSelect,
  (prevProps, nextProps) =>
    prevProps.showSelectAllOption == nextProps.showSelectAllOption &&
    prevProps.className == nextProps.className &&
    prevProps.readOnly == nextProps.readOnly &&
    prevProps.getLabel == nextProps.getLabel &&
    prevProps.options == nextProps.options
)
function MultiSelect(props, _context) {
  const { getLabel, readOnly, className, showSelectAllOption, options: optionsProp } = props

  const [options, setOptions] = useState(initOptions(optionsProp, showSelectAllOption))

  // update options on optionsProp change
  useEffect(() => setOptions(initOptions(optionsProp, showSelectAllOption)), [optionsProp, showSelectAllOption])

  const fetchOptions = useCallback(() => options, [options])
  const onSelect = useCallback(option => setOptions(updateOptions(options, option.get('value'))), [options])
  const label = useMemo(
    () => ('function' == typeof getLabel ? getLabel(options) : 'Please select'),
    [getLabel, options]
  )
  return (
    <div className={classNames('rfd-ui-multi-select', className, { readOnly: readOnly })}>
      <InlineSelect
        disabled={Boolean(readOnly)}
        onSelect={onSelect}
        fetchOptions={fetchOptions}
        hideOptionsOnSelect={false}
      >
        {label}
      </InlineSelect>
    </div>
  )
}

function setCheckBoxRender(option) {
  return option.set('render', () => <CheckBox label={option.get('text')} checked={option.get('selected')} />)
}

function initOptions(options = List(), showSelectAllOption = false) {
  return List(
    options.reduce((acc, _option) => {
      const option = setCheckBoxRender(Map.isMap(_option) ? _option : Map(_option))

      // insert "select all" option
      if (showSelectAllOption && 0 == acc.length) {
        const selected = option.get('selected')
        const selectAllOption = isOptionsSimilar(options)
          ? Map({ value: 'all', text: selected ? 'Deselect all' : 'Select all', selected })
          : Map({ value: 'all', text: 'Deselect all', selected: true })
        acc.push(setCheckBoxRender(selectAllOption))
      }

      const value = option.get('value')
      acc.push('string' == typeof value ? option : option.set('value', value.toString()))
      return acc
    }, [])
  )
}

function updateOptions(options, optionValue) {
  // update all options
  if ('all' == optionValue) {
    const selectAll = !options.getIn([0, 'selected'])
    return options.map(option => {
      const selected = option.get('selected')
      const updatedOption =
        'all' == option.get('value') ? option.set('text', selected ? 'Select all' : 'Deselect all') : option
      return setCheckBoxRender(selectAll == selected ? updatedOption : updatedOption.set('selected', !selected))
    })
  }

  // update single option
  const index = options.findIndex(option => optionValue == option.get('value'))
  if (-1 == index) return options

  const updatedOptions = options.update(index, option =>
    setCheckBoxRender(option.update('selected', selected => !selected))
  )

  const selectAllOption = 'all' == options.getIn([0, 'value']) ? options.get(0) : null
  return selectAllOption &&
    isOptionsSimilar(updatedOptions.skip(1)) &&
    selectAllOption.get('selected') != updatedOptions.getIn([1, 'selected'])
    ? updatedOptions.update(0, option =>
        setCheckBoxRender(
          option
            .set('selected', !option.get('selected'))
            .set('text', option.get('selected') ? 'Select all' : 'Deselect all')
        )
      )
    : updatedOptions
}

function isOptionsSimilar(options) {
  const selected = options.getIn([0, 'selected'])
  return options.skip(1).every(option => selected == option.get('selected'))
}
